import * as Model from "../../../model";

export default class Operations {
    localAuthority: Model.LocalAuthority = null;
    ppi: Model.Ppi = null;
    scenario: Model.Scenario = null;
    values: Values = {};

    // Pre-indexed lookup maps
    private _obsMap: Map<string, Model.Observation> = null;
    private _scenarioYearMap: Map<number, any> = null;
    private _scenarioYearAccountMaps: Map<number, Map<string, any>> = null;
    private _scenarioAccountMap: Map<string, any> = null;
    private _scenarioChapterMap: Map<string, any> = null;
    private _inducedIndex: Map<string, Model.ProjectInduced[]> = null;
    private _previousScenarioOps: Map<number, Operations> = new Map();

    constructor(
        scenario: Model.Scenario,
        ppi: Model.Ppi,
        localAuthority: Model.LocalAuthority
    ) {
        this.scenario = scenario;
        this.ppi = ppi;
        this.localAuthority = localAuthority;
        this._buildIndexes();
    }

    private _buildIndexes(): void {
        // Observation map: "year-accountId" -> observation
        this._obsMap = new Map();
        if (this.localAuthority.observations) {
            for (const o of this.localAuthority.observations) {
                const key = `${o.year}-${o.account.id}`;
                if (!this._obsMap.has(key)) {
                    this._obsMap.set(key, o);
                }
            }
        }

        // Scenario year map: year -> scenarioYear
        this._scenarioYearMap = new Map();
        this._scenarioYearAccountMaps = new Map();
        if (this.scenario && this.scenario.years) {
            for (const y of this.scenario.years) {
                if (!this._scenarioYearMap.has(y.year)) {
                    this._scenarioYearMap.set(y.year, y);
                }
                // Per-year account map: "accountId-receipts" -> scenarioYearAccount
                const accMap = new Map();
                if (y.accounts) {
                    for (const a of y.accounts) {
                        const key = `${a.account_id}-${a.receipts}`;
                        if (!accMap.has(key)) {
                            accMap.set(key, a);
                        }
                        // Also store with undefined receipts key
                        const keyUndef = `${a.account_id}-undefined`;
                        if (!accMap.has(keyUndef)) {
                            accMap.set(keyUndef, a);
                        }
                    }
                }
                this._scenarioYearAccountMaps.set(y.year, accMap);
            }
        }

        // Scenario account map: "accountId-receipts" -> scenarioAccount
        this._scenarioAccountMap = new Map();
        if (this.scenario && this.scenario.accounts) {
            for (const a of this.scenario.accounts) {
                const key = `${a.account_id}-${a.receipts}`;
                if (!this._scenarioAccountMap.has(key)) {
                    this._scenarioAccountMap.set(key, a);
                }
                const keyUndef = `${a.account_id}-undefined`;
                if (!this._scenarioAccountMap.has(keyUndef)) {
                    this._scenarioAccountMap.set(keyUndef, a);
                }
            }
        }

        // Scenario chapter map: "chapterId-receipts" -> scenarioChapter
        this._scenarioChapterMap = new Map();
        if (this.scenario && this.scenario.chapters) {
            for (const c of this.scenario.chapters) {
                const key = `${c.chapter_id}-${c.receipts}`;
                if (!this._scenarioChapterMap.has(key)) {
                    this._scenarioChapterMap.set(key, c);
                }
                const keyUndef = `${c.chapter_id}-undefined`;
                if (!this._scenarioChapterMap.has(keyUndef)) {
                    this._scenarioChapterMap.set(keyUndef, c);
                }
            }
        }

        // Induced index: "year-accountId" -> induceds[]
        this._inducedIndex = new Map();
        if (this.ppi && this.ppi.investment_themes) {
            for (const theme of this.ppi.investment_themes) {
                for (const project of theme.projects) {
                    if (!project.visible) continue;
                    if (project.induceds) {
                        for (const ind of project.induceds) {
                            const key = `${ind.year}-${ind.induced.id}`;
                            if (!this._inducedIndex.has(key)) {
                                this._inducedIndex.set(key, []);
                            }
                            this._inducedIndex.get(key).push({ ...ind, project });
                        }
                    }
                }
                if (theme.themes) {
                    for (const subtheme of theme.themes) {
                        for (const project of subtheme.projects) {
                            if (!project.visible) continue;
                            if (project.induceds) {
                                for (const ind of project.induceds) {
                                    const key = `${ind.year}-${ind.induced.id}`;
                                    if (!this._inducedIndex.has(key)) {
                                        this._inducedIndex.set(key, []);
                                    }
                                    this._inducedIndex.get(key).push({ ...ind, project });
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    getObservation(year: number, accountId: number): Model.Observation | undefined {
        return this._obsMap.get(`${year}-${accountId}`);
    }

    findValue(
        year: number,
        account_id: number,
        chapter_id: number,
        receipts: boolean,
        previous_scenario: Model.Scenario = null,
        priorities: number[] = []
    ): number {
        const cacheKey = `${year}-${account_id}-${chapter_id}-${receipts}-${previous_scenario ? previous_scenario.id : ""}`;

        if (this.values[cacheKey] !== undefined) {
            return this.values[cacheKey];
        }

        var value = 0;
        var manageInduceds = true;

        if (year == this.scenario.year - 1) {
            if (previous_scenario) {
                // Reuse cached Operations instance for previous scenario
                if (!this._previousScenarioOps.has(previous_scenario.id)) {
                    this._previousScenarioOps.set(
                        previous_scenario.id,
                        new Operations(previous_scenario, this.ppi, this.localAuthority)
                    );
                }
                value = this._previousScenarioOps.get(previous_scenario.id)
                    .findValue(year, account_id, chapter_id, receipts);
            } else {
                const obs = this._obsMap.get(`${year}-${account_id}`);
                if (obs) value = obs.amount;
            }
        } else {
            value = this.findValue(year - 1, account_id, chapter_id, receipts, previous_scenario);

            const scenarioYear = this._scenarioYearMap.get(year);
            let scenarioYearAccount = null;

            if (scenarioYear) {
                const accMap = this._scenarioYearAccountMaps.get(year);
                if (accMap) {
                    const key = receipts === undefined ? `${account_id}-undefined` : `${account_id}-${receipts}`;
                    scenarioYearAccount = accMap.get(key);
                }
            }

            if (scenarioYearAccount && scenarioYearAccount.amount !== null) {
                if (scenarioYearAccount.percentile) {
                    value = this.findValue(year - 1, account_id, chapter_id, receipts, previous_scenario);
                    value += (value * scenarioYearAccount.amount) / 100;
                } else {
                    value = scenarioYearAccount.amount;
                    manageInduceds = false;
                }
            } else {
                const accKey = receipts === undefined ? `${account_id}-undefined` : `${account_id}-${receipts}`;
                const account = this._scenarioAccountMap.get(accKey);
                if (account) {
                    value += (value * account.amount) / 100;
                } else {
                    const chapKey = receipts === undefined ? `${chapter_id}-undefined` : `${chapter_id}-${receipts}`;
                    const chapter = this._scenarioChapterMap.get(chapKey);
                    if (chapter) {
                        value += (value * chapter.amount) / 100;
                    }
                }
            }
        }

        if (manageInduceds && !this._obsMap.has(`${year}-${account_id}`)) {
            const induced = this.findInduceds(year, account_id, priorities);
            for (const i of induced) {
                value += (i.gain ? i.gain : 0) - (i.loss ? i.loss : 0);
            }
        }

        this.values[cacheKey] = value;
        return value;
    }

    findInduceds(
        year: number,
        accountId: number,
        priorities: number[]
    ): Model.ProjectInduced[] {
        const key = `${year}-${accountId}`;
        const allInduceds = this._inducedIndex.get(key);
        if (!allInduceds || allInduceds.length === 0) return [];

        if (!priorities || priorities.length === 0) return allInduceds;

        return allInduceds.filter(
            (ind) => priorities.some((p) => p === (ind as any).project.priority)
        );
    }
}

interface Values {
    [key: string]: number;
}



// WEBPACK FOOTER //
// ./src/component/scenarii/common/operations.ts